import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { toast } from "sonner";

const REL_OPTIONS = [
  { value: "on_track", label: "On Track", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  { value: "needs_attention", label: "Needs Attention", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  { value: "stalled", label: "Stalled", cls: "bg-red-500/10 text-red-300 border-red-500/30" },
];

const inputCls = "w-full rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs text-neutral-100 outline-none focus:border-[#0A8B85]";

const RM_STATUS_TXT = {
  paused: "Paused", non_responsive: "Non-Responsive",
  not_ready: "Not Ready to Raise", other: "Other",
};

function Row({ r, onSave, onContacted, rmFlag }) {
  const [advice, setAdvice] = useState(r.advice_given || "");
  const [steps, setSteps] = useState(r.next_steps || "");
  const id = r.company_id;
  return (
    <tr data-testid={`hvc-row-${id}`} className="align-top">
      <td className="px-3 py-3">
        <p className="font-semibold text-neutral-100">{r.founder_name}</p>
        <p className="text-[11px] text-neutral-500">{r.founder_email}</p>
        {rmFlag && (
          <span title={rmFlag.reason ? `Reason: ${rmFlag.reason}` : undefined}
            className="mt-1 inline-block rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-300"
            data-testid={`hvc-rm-flag-${id}`}>
            ⚑ RM: Active → {RM_STATUS_TXT[rmFlag.to_status] || rmFlag.to_status}
          </span>
        )}
      </td>
      <td className="px-3 py-3 text-neutral-300">{r.company_name}</td>
      <td className="px-3 py-3">
        <span data-testid={`hvc-tier-${id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
          r.tier === "platinum" ? "bg-violet-500/10 text-violet-300" : "bg-yellow-500/10 text-yellow-300"}`}>
          {r.tier}
        </span>
      </td>
      <td className="px-3 py-3 text-center font-bold text-neutral-100" data-testid={`hvc-stage-${id}`}>{r.stage}</td>
      <td className="px-3 py-3 text-center font-bold text-[#2dd4bf]">{r.crs_score ?? "—"}</td>
      <td className="px-3 py-3 text-center">{r.report_generated
        ? <span className="text-emerald-400 font-semibold text-xs">Yes</span>
        : <span className="text-neutral-500 text-xs">No</span>}</td>
      <td className="px-3 py-3">
        <select data-testid={`hvc-review-${id}`} value={r.advisor_review_completed ? "yes" : "no"}
          onChange={(e) => onSave(id, { advisor_review_completed: e.target.value === "yes" })}
          className={inputCls}>
          <option value="no">No</option>
          <option value="yes">Yes</option>
        </select>
      </td>
      <td className="px-3 py-3 min-w-[160px]">
        <textarea data-testid={`hvc-advice-${id}`} rows={2} value={advice}
          onChange={(e) => setAdvice(e.target.value)}
          onBlur={() => { if (advice !== (r.advice_given || "")) onSave(id, { advice_given: advice }); }}
          placeholder="Advice given…" className={inputCls} />
      </td>
      <td className="px-3 py-3">
        <input data-testid={`hvc-last-meeting-${id}`} type="date" value={r.last_meeting_date || ""}
          onChange={(e) => onSave(id, { last_meeting_date: e.target.value })} className={inputCls} />
      </td>
      <td className="px-3 py-3">
        <input data-testid={`hvc-next-meeting-${id}`} type="date" value={r.next_meeting_date || ""}
          onChange={(e) => onSave(id, { next_meeting_date: e.target.value })} className={inputCls} />
      </td>
      <td className="px-3 py-3 min-w-[160px]">
        <textarea data-testid={`hvc-steps-${id}`} rows={2} value={steps}
          onChange={(e) => setSteps(e.target.value)}
          onBlur={() => { if (steps !== (r.next_steps || "")) onSave(id, { next_steps: steps }); }}
          placeholder="Next steps…" className={inputCls} />
      </td>
      <td className="px-3 py-3">
        <button data-testid={`hvc-tasked-${id}`}
          onClick={() => onSave(id, { next_steps_tasked_to: r.next_steps_tasked_to === "advisor" ? "founder" : "advisor" })}
          className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase ${
            r.next_steps_tasked_to === "advisor"
              ? "border-sky-500/40 bg-sky-500/10 text-sky-300"
              : "border-teal-500/40 bg-teal-500/10 text-teal-300"}`}>
          {r.next_steps_tasked_to === "advisor" ? "Advisor" : "Founder"}
        </button>
      </td>
      <td className="px-3 py-3">
        {r.meeting_initiated_at ? (
          <div data-testid={`hvc-initiated-${id}`}>
            <span className="text-xs font-semibold text-emerald-400">Yes</span>
            <p className="text-[10px] text-neutral-500">{r.meeting_initiated_at.slice(0, 10)}</p>
          </div>
        ) : (
          <span data-testid={`hvc-initiated-${id}`} className="text-xs text-neutral-500">No</span>
        )}
        {r.meeting_initiated_note && (
          <p data-testid={`hvc-initiated-note-${id}`} className="mt-0.5 text-[10px] font-semibold text-amber-400">{r.meeting_initiated_note}</p>
        )}
        {(r.outreach_emails || []).length > 0 && (
          <div className="mt-1 space-y-0.5" data-testid={`hvc-outreach-log-${id}`}>
            {r.outreach_emails.map((e) => (
              <p key={e.n} className="text-[10px] text-neutral-500" title={e.subject}>
                ✉ Email {e.n} · {(e.sent_at || "").slice(0, 10)}
              </p>
            ))}
          </div>
        )}
        <button data-testid={`hvc-contacted-${id}`} onClick={() => onContacted(id)}
          className="mt-1.5 block rounded-md border border-[#0A8B85]/50 px-2 py-1 text-[10px] font-bold text-[#2dd4bf] hover:bg-[#0A8B85]/10">
          Mark as Contacted
        </button>
      </td>
      <td className="px-3 py-3">
        <select data-testid={`hvc-status-${id}`} value={r.relationship_status || ""}
          onChange={(e) => onSave(id, { relationship_status: e.target.value })}
          className={`rounded-md border px-2 py-1 text-[11px] font-semibold outline-none ${
            (REL_OPTIONS.find((o) => o.value === r.relationship_status) || {}).cls
            || "border-neutral-700 bg-neutral-900 text-neutral-400"}`}>
          <option value="">— Set status —</option>
          {REL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>
    </tr>
  );
}

export default function AdminHVCTracker() {
  const [rows, setRows] = useState([]);
  const [rmFlags, setRmFlags] = useState({});
  const [tab, setTab] = useState("gold");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/hvc-tracker");
      setRows(data.rows || []);
    } catch (_) { toast.error("Couldn't load the tracker"); }
    try {
      const { data } = await api.get("/admin/rm-status-flags");
      const map = {};
      (data.flags || []).forEach((f) => { if (!map[f.company_id]) map[f.company_id] = f; });
      setRmFlags(map);
    } catch (_) { /* advisor without access or none */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (companyId, fields) => {
    setRows((rs) => rs.map((r) => (r.company_id === companyId ? { ...r, ...fields } : r)));
    try {
      await api.put(`/admin/hvc-tracker/${companyId}`, fields);
    } catch (_) { toast.error("Couldn't save — try again"); }
  };

  const markContacted = async (companyId) => {
    try {
      const { data } = await api.post(`/admin/hvc-tracker/${companyId}/mark-contacted`);
      setRows((rs) => rs.map((r) => (r.company_id === companyId
        ? { ...r, meeting_initiated_at: data.meeting_initiated_at } : r)));
      toast.success("Marked as contacted");
    } catch (_) { toast.error("Couldn't update — try again"); }
  };

  const shown = rows.filter((r) => r.tier === tab);
  const goldN = rows.filter((r) => r.tier === "gold").length;
  const platN = rows.filter((r) => r.tier === "platinum").length;

  return (
    <AdminShell current="/admin/hvc-tracker">
      <div className="px-6 py-6">
        <h1 className="text-xl font-bold text-white" data-testid="hvc-title">High-Value Client Tracker</h1>
        <p className="mt-1 text-sm text-neutral-400">
          Gold = KES 1M–5M monthly revenue · Platinum = above KES 5M. Tiers update automatically from onboarding revenue.
        </p>

        <div className="mt-5 flex gap-2">
          <button data-testid="hvc-tab-gold" onClick={() => setTab("gold")}
            className={`rounded-full px-4 py-1.5 text-sm font-bold ${tab === "gold"
              ? "bg-yellow-500/20 text-yellow-300 ring-1 ring-yellow-500/40" : "bg-neutral-900 text-neutral-400 hover:text-neutral-200"}`}>
            Gold ({goldN})
          </button>
          <button data-testid="hvc-tab-platinum" onClick={() => setTab("platinum")}
            className={`rounded-full px-4 py-1.5 text-sm font-bold ${tab === "platinum"
              ? "bg-violet-500/20 text-violet-300 ring-1 ring-violet-500/40" : "bg-neutral-900 text-neutral-400 hover:text-neutral-200"}`}>
            Platinum ({platN})
          </button>
        </div>

        <div className="mt-4 overflow-x-auto rounded-2xl border border-neutral-800">
          <table className="w-full min-w-[1500px] text-left text-sm" data-testid="hvc-table">
            <thead className="bg-neutral-900 text-[10px] uppercase tracking-wider text-neutral-400">
              <tr>
                <th className="px-3 py-3">Founder</th>
                <th className="px-3 py-3">Company</th>
                <th className="px-3 py-3">Tier</th>
                <th className="px-3 py-3">Stage</th>
                <th className="px-3 py-3">CRS</th>
                <th className="px-3 py-3">Report</th>
                <th className="px-3 py-3">Advisor Review</th>
                <th className="px-3 py-3">Advice Given</th>
                <th className="px-3 py-3">Last Meeting</th>
                <th className="px-3 py-3">Next Meeting</th>
                <th className="px-3 py-3">Next Steps</th>
                <th className="px-3 py-3">Tasked To</th>
                <th className="px-3 py-3">Meeting Initiated</th>
                <th className="px-3 py-3">Relationship</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800 bg-neutral-950">
              {loading ? (
                <tr><td colSpan="14" className="px-4 py-10 text-center text-neutral-500">Loading…</td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="14" className="px-4 py-10 text-center text-neutral-500">No {tab} founders yet.</td></tr>
              ) : shown.map((r) => (
                <Row key={r.company_id} r={r} onSave={save} onContacted={markContacted} rmFlag={rmFlags[r.company_id]} />
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-neutral-600">Changes save automatically.</p>
      </div>
    </AdminShell>
  );
}
